import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type {
  AuthChangeEvent,
  AuthError,
  Session,
  SupabaseClient,
  User,
} from '@supabase/supabase-js';
import { supabase as defaultClient } from '../supabase';
import { extractTenantClaims, type TenantClaims } from './jwt';

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

export type AuthContextValue = {
  status: AuthStatus;
  session: Session | null;
  user: User | null;
  tenantId: string | null;
  userType: TenantClaims['userType'];
  // True iff the user has signed in. Single source of truth — components
  // should NOT call supabase.auth.getSession() directly.
  isAuthenticated: boolean;
  // True until the very first auth state event has resolved. Routes use
  // this to decide whether to hold a redirect.
  isReady: boolean;
  signInWithPassword: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signUpWithPassword: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  setPassword: (password: string) => Promise<{ error: AuthError | null }>;
  requestPasswordReset: (email: string, redirectTo: string) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

type AuthProviderProps = {
  children: ReactNode;
  client?: SupabaseClient;
};

function deriveClaims(session: Session | null): TenantClaims {
  return extractTenantClaims(session?.access_token ?? null);
}

export function AuthProvider({ children, client = defaultClient }: AuthProviderProps): JSX.Element {
  const [session, setSession] = useState<Session | null>(null);
  const [isReady, setIsReady] = useState(false);
  // Held in a ref because both the bootstrap fetch AND the auth-state
  // subscription set state — without a ref, a slow getSession() can
  // overwrite a faster onAuthStateChange event. Last writer wins by
  // arrival order, which is what we want.
  const latestSessionId = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const applySession = (incoming: Session | null) => {
      if (cancelled) return;
      const id = incoming?.access_token ?? null;
      latestSessionId.current = id;
      setSession(incoming);
      setIsReady(true);
    };

    void client.auth.getSession().then(({ data }) => {
      // Only apply if no auth-state event has already won.
      if (latestSessionId.current === null) applySession(data.session);
    });

    const sub = client.auth.onAuthStateChange((_event: AuthChangeEvent, next) => {
      applySession(next ?? null);
    });

    return () => {
      cancelled = true;
      sub.data.subscription.unsubscribe();
    };
  }, [client]);

  const claims = useMemo(() => deriveClaims(session), [session]);

  const signInWithPassword = useCallback<AuthContextValue['signInWithPassword']>(
    async (email, password) => {
      const { error } = await client.auth.signInWithPassword({ email, password });
      return { error };
    },
    [client],
  );

  const signUpWithPassword = useCallback<AuthContextValue['signUpWithPassword']>(
    async (email, password) => {
      const { error } = await client.auth.signUp({ email, password });
      return { error };
    },
    [client],
  );

  const setPassword = useCallback<AuthContextValue['setPassword']>(
    async (password) => {
      const { error } = await client.auth.updateUser({ password });
      return { error };
    },
    [client],
  );

  const requestPasswordReset = useCallback<AuthContextValue['requestPasswordReset']>(
    async (email, redirectTo) => {
      const { error } = await client.auth.resetPasswordForEmail(email, { redirectTo });
      return { error };
    },
    [client],
  );

  const signOut = useCallback<AuthContextValue['signOut']>(async () => {
    await client.auth.signOut();
  }, [client]);

  const value = useMemo<AuthContextValue>(() => {
    const status: AuthStatus = !isReady ? 'loading' : session ? 'authenticated' : 'unauthenticated';
    return {
      status,
      session,
      user: session?.user ?? null,
      tenantId: claims.tenantId,
      userType: claims.userType,
      isAuthenticated: status === 'authenticated',
      isReady,
      signInWithPassword,
      signUpWithPassword,
      setPassword,
      requestPasswordReset,
      signOut,
    };
  }, [
    isReady,
    session,
    claims.tenantId,
    claims.userType,
    signInWithPassword,
    signUpWithPassword,
    setPassword,
    requestPasswordReset,
    signOut,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth() must be used inside <AuthProvider>');
  }
  return ctx;
}

// Claims-only selector for code paths (RLS-aware queries) that don't
// need the full auth surface. Returns just the JWT claims — the full
// tenant row lives behind useTenant() from ../../context/TenantContext.
// Throws if called outside the provider, same invariant as useAuth.
export function useTenantClaims(): {
  tenantId: string | null;
  userType: TenantClaims['userType'];
} {
  const { tenantId, userType } = useAuth();
  return { tenantId, userType };
}

